import {
	CameraTwoTone,
	DeleteOutlined,
	ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	Button,
	Divider,
	Form,
	Input,
	message,
	Modal,
	Radio,
	Select,
	Upload,
} from "antd";
import * as API from "common/api";
import * as COMMONS from "common/common";
import { TapAnimationComponent } from "components";
import {
	ImageTemplateContainer,
	SelectLayout,
	SelectLayoutContext,
} from "components/common/SelectLayout";
import {
	INIT_DATA_USER_DEFAULT,
	INIT_DATA_USER_MEMBER,
	SET_DATA_DEFAULT_USER,
	SET_DATA_MEMBER_USER,
} from "components/common/SelectLayout/SelectLayout.action";
import { motion } from "framer-motion";
import { Fragment, memo, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { get, isEqual } from "lodash";

export const INPUT_LABEL = [
	"A部分のリンク先",
	"B部分のリンク先",
	"C部分のリンク先",
	"D部分のリンク先",
	"E部分のリンク先",
	"F部分のリンク先",
];

/**
 * @param {object} props
 * @param {string | string[]} props.fieldNameWatched
 * @param {string | string[]} props.fieldNameValidated
 * @returns {JSX.Element}
 */
const TriggerValidation = memo(
	({ fieldNameWatched, fieldNameValidated }) => {
		const value = Form.useWatch(fieldNameWatched);
		const validatedValue = Form.useWatch(fieldNameValidated);
		const form = Form.useFormInstance();

		useEffect(() => {
			if (!value || !validatedValue) return;

			form.validateFields([fieldNameValidated]);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [value, validatedValue]);

		return null;
	},
	(prev, next) => {
		return JSON.stringify(prev) === JSON.stringify(next);
	},
);

/**
 * @param {object} props
 * @param {string} props.name
 * @returns {JSX.Element}
 */
const AddonBefore = ({ name }) => {
	return (
		<Form.Item name={name} initialValue="uri" noStyle>
			<Select
				style={{ width: 120 }}
				options={[
					{
						label: "リンク",
						value: "uri",
					},
					{
						label: "メッセージ",
						value: "message",
					},
				]}
			/>
		</Form.Item>
	);
};

const INPUT_NAME = ["linkA", "linkB", "linkC", "linkD", "linkE", "linkF"];

const getImageArea = (width, height) => ({
	compact: {
		"richmenu-template-guidem-01.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: 0,
						width: width / 3,
						height: height,
					},
				},
				{
					bounds: {
						x: width / 3,
						y: 0,
						width: width / 3,
						height: height,
					},
				},
				{
					bounds: {
						x: (width / 3) * 2,
						y: 0,
						width: width / 3,
						height: height,
					},
				},
			],
		},
		"richmenu-template-guidem-02.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: 0,
						width: width / 2,
						height: height,
					},
				},
				{
					bounds: {
						x: width / 2,
						y: 0,
						width: width / 2,
						height: height,
					},
				},
			],
		},
		"richmenu-template-guidem-03.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: 0,
						width: width / 2,
						height: height,
					},
				},
				{
					bounds: {
						x: width / 2,
						y: 0,
						width: width / 3,
						height: height,
					},
				},
			],
		},
		"richmenu-template-guidem-04.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: 0,
						width: width / 3,
						height: height,
					},
				},
				{
					bounds: {
						x: width / 3,
						y: 0,
						width: width / 2,
						height: height,
					},
				},
			],
		},
		"richmenu-template-guidem-05.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: 0,
						width: width,
						height: height,
					},
				},
			],
		},
	},
	large: {
		"richmenu-template-guidem-01.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: 0,
						width: width / 3,
						height: height / 2,
					},
				},
				{
					bounds: {
						x: width / 3,
						y: 0,
						width: width / 3,
						height: height / 2,
					},
				},
				{
					bounds: {
						x: (width / 3) * 2,
						y: 0,
						width: width / 3,
						height: height / 2,
					},
				},
				{
					bounds: {
						x: 0,
						y: height / 2,
						width: width / 3,
						height: height / 2,
					},
				},
				{
					bounds: {
						x: width / 3,
						y: height / 2,
						width: width / 3,
						height: height / 2,
					},
				},
				{
					bounds: {
						x: (width / 3) * 2,
						y: height / 2,
						width: width / 3,
						height: height / 2,
					},
				},
			],
		},
		"richmenu-template-guidem-02.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: 0,
						width: width / 2,
						height: height / 2,
					},
				},
				{
					bounds: {
						x: width / 2,
						y: 0,
						width: width / 2,
						height: height / 2,
					},
				},
				{
					bounds: {
						x: 0,
						y: height / 2,
						width: width / 2,
						height: height / 2,
					},
				},
				{
					bounds: {
						x: width / 2,
						y: height / 2,
						width: width / 2,
						height: height / 2,
					},
				},
			],
		},
		"richmenu-template-guidem-03.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: 0,
						width: width,
						height: height / 2,
					},
				},
				{
					bounds: {
						x: 0,
						y: height / 2,
						width: width / 3,
						height: height / 2,
					},
				},
				{
					bounds: {
						x: width / 3,
						y: height / 2,
						width: width / 3,
						height: height / 2,
					},
				},
				{
					bounds: {
						x: (width / 3) * 2,
						y: height / 2,
						width: width / 3,
						height: height / 2,
					},
				},
			],
		},
		"richmenu-template-guidem-04.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: 0,
						width: (width / 3) * 2,
						height: height,
					},
				},
				{
					bounds: {
						x: (width / 3) * 2,
						y: 0,
						width: width / 3,
						height: height / 2,
					},
				},
				{
					bounds: {
						x: (width / 3) * 2,
						y: height / 2,
						width: width / 3,
						height: height / 2,
					},
				},
			],
		},
		"richmenu-template-guidem-05.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: 0,
						width: width,
						height: height / 2,
					},
				},
				{
					bounds: {
						x: 0,
						y: height / 2,
						width: width,
						height: height / 2,
					},
				},
			],
		},
		"richmenu-template-guidem-06.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: 0,
						width: width / 2,
						height: height,
					},
				},
				{
					bounds: {
						x: width / 2,
						y: 0,
						width: width / 2,
						height: height,
					},
				},
			],
		},
		"richmenu-template-guidem-07.png": {
			areas: [
				{
					bounds: {
						x: 0,
						y: 0,
						width: width,
						height: height,
					},
				},
			],
		},
	},
});

const checkRichMenuImageSize = (imgType, type, param, callback) => {
	const isSt1M = param.file.size / 1024 / 1024 < 1;

	if (isSt1M) {
		const reader = new FileReader();
		reader.readAsDataURL(param.file);
		reader.addEventListener("load", (event) => {
			const _loadedImageUrl = event.target.result;
			const image = document.createElement("img");

			image.src = _loadedImageUrl;
			image.addEventListener("load", async () => {
				const { width, height } = image;

				if (type === "defaultRM") {
					if (
						(imgType === "compact" && width === 1200 && height === 405) ||
						(imgType === "large" && width === 1200 && height === 810)
					) {
						callback({
							file: param.file,
							preview: _loadedImageUrl,
						});
					} else {
						message.warning(COMMONS.WARN_IMAGE_SIZE_DIFFERENT);
					}
				} else if (type === "memberRM") {
					if (
						(imgType === "compact" && width === 1200 && height === 405) ||
						(imgType === "large" && width === 1200 && height === 810)
					) {
						callback({
							file: param.file,
							preview: _loadedImageUrl,
						});
					} else {
						message.warning(COMMONS.WARN_IMAGE_SIZE_DIFFERENT);
					}
				}
			});
		});
	} else {
		message.warning(COMMONS.WARN_IMAGE_TOO_BIG);
	}
};

const CustomUpload = styled(Upload)`
	.ant-upload {
		width: 100%;
	}
`;

const RichmenuModal = (props) => {
	const {
		publicSettings,
		isRichmenuModalVisible,
		hideRichmenuModal,
		richmenus,
	} = props;

	const isMountedRef = COMMONS.USE_IS_MOUNTED_REF();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [defaultRichmenuForm] = Form.useForm();
	const [memberRichmenuForm] = Form.useForm();

	const [defaultRichmenuImageFile, setDefaultRichmenuImageFile] =
		useState(undefined);
	const [memberRichmenuImageFile, setMemberRichmenuImageFile] =
		useState(undefined);

	const [modal, contextHolder] = Modal.useModal();

	//rich menu
	const {
		dispatch,
		state,
		handleSelectTemplate,
		setInitialValue,
		setIsOpenImageTemplate,
		isOpenImageTemplate,
		selected,
	} = useContext(SelectLayoutContext);
	useEffect(() => {
		if (state.default.imgSelect || state.member.imgSelect) {
			const defaultImage = state.default.imgSelect?.split("/")[3];

			const memberImage = state.member.imgSelect?.split("/")[3];

			const area =
				selected === "default"
					? getImageArea(
							state.default.dimension.width,
							state.default.dimension.height,
						)
					: getImageArea(
							state.member.dimension.width,
							state.member.dimension.height,
						);

			selected === "default"
				? dispatch({
						type: SET_DATA_DEFAULT_USER,
						payload: {
							...state.default,
							areas: area[state.default.imgType][defaultImage],
						},
					})
				: dispatch({
						type: SET_DATA_MEMBER_USER,
						payload: {
							...state.member,
							areas: area[state.member.imgType][memberImage],
						},
					});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		state.default.dimension,
		state.member.dimension,
		state.default.imgSelect,
		state.member.imgSelect,
	]);

	const richmenuUpdateMutation = useMutation(API.ADMIN_UPDATE_RICHMENU, {
		onSuccess: () => {
			if (isMountedRef.current) {
				message.success(COMMONS.SUCCESS_UPDATE_MSG);
				queryClient.invalidateQueries({
					queryKey: [API.QUERY_KEY_ADMIN_RICHMENUS],
				});
			}
		},
		onError: (error) => {
			if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
				navigate(COMMONS.PERMISSION_ERROR_ROUTE);
			} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
				message.warning({
					content: COMMONS.ERROR_SESSION_MSG,
					key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
				});
				navigate(COMMONS.ADMIN_LOGIN_ROUTE);
			} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
				message.error({
					content: COMMONS.ERROR_SYSTEM_MSG,
					key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
				});
			} else {
				message.error({
					content: COMMONS.ERROR_SYSTEM_MSG,
					key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
				});
			}
		},
	});

	const richmenuDeleteMutation = useMutation(API.ADMIN_DELETE_RICHMENU, {
		onSuccess: (data, variables) => {
			const { type } = variables;

			if (isMountedRef.current) {
				message.success(COMMONS.SUCCESS_DELETE_MSG);
				queryClient.invalidateQueries({
					queryKey: [API.QUERY_KEY_ADMIN_RICHMENUS],
				});
			}
			if (type === "defaultRM") {
				dispatch({
					type: INIT_DATA_USER_DEFAULT,
				});
			} else {
				dispatch({
					type: INIT_DATA_USER_MEMBER,
				});
			}
		},
		onError: (error) => {
			if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
				navigate(COMMONS.PERMISSION_ERROR_ROUTE);
			} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
				message.warning({
					content: COMMONS.ERROR_SESSION_MSG,
					key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
				});
				navigate(COMMONS.ADMIN_LOGIN_ROUTE);
			} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
				message.error({
					content: COMMONS.ERROR_SYSTEM_MSG,
					key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
				});
			} else {
				message.error({
					content: COMMONS.ERROR_SYSTEM_MSG,
					key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
				});
			}
		},
	});

	useEffect(() => {
		if (!isRichmenuModalVisible) {
			defaultRichmenuForm.resetFields();
			memberRichmenuForm.resetFields();

			setDefaultRichmenuImageFile(undefined);
			setMemberRichmenuImageFile(undefined);
		}

		// eslint-disable-next-line
	}, [isRichmenuModalVisible]);

	useEffect(() => {
		if (isRichmenuModalVisible) {
			if (richmenus?.defaultRM?.picUrl) {
				const initialValue = {
					richmenuImage: richmenus.defaultRM.picUrl,
					...["A", "B", "C", "D", "E", "F"].reduce((acc, cur, index) => {
						const action = get(richmenus, `defaultRM.actions.${index}`);
						return {
							...acc,
							[`link${cur}`]: action
								? {
										type: action.type,
										value: action.type === "message" ? action.text : action.uri,
										position: action.position,
									}
								: undefined,
						};
					}, {}),
				};
				defaultRichmenuForm.setFieldsValue(initialValue);

				setDefaultRichmenuImageFile({
					preview: `${API.SETTINGS_UPLOADS_URL}${richmenus.defaultRM.picUrl}`,
				});
			} else {
				defaultRichmenuForm.resetFields();

				setDefaultRichmenuImageFile(undefined);
			}

			if (richmenus?.memberRM?.picUrl) {
				const initialValue = {
					richmenuImage: richmenus.memberRM.picUrl,
					...["A", "B", "C", "D", "E", "F"].reduce((acc, cur, index) => {
						const action = get(richmenus, `memberRM.actions.${index}`);
						return {
							...acc,
							[`link${cur}`]: action
								? {
										type: action.type,
										value: action.type === "message" ? action.text : action.uri,
										position: action.position,
									}
								: undefined,
						};
					}, {}),
				};

				memberRichmenuForm.setFieldsValue(initialValue);

				setMemberRichmenuImageFile({
					preview: `${API.SETTINGS_UPLOADS_URL}${richmenus.memberRM.picUrl}`,
				});
			} else {
				memberRichmenuForm.resetFields();

				setMemberRichmenuImageFile(undefined);
			}
		}

		// eslint-disable-next-line
	}, [richmenus, isRichmenuModalVisible]);

	const handleRichmenuUpdate = (type, field) => () => {
		if (type === "defaultRM") {
			const data = defaultRichmenuForm.getFieldsValue(true);

			const paramData = {
				type: type,
				isDisplayed: richmenus.defaultRM?.isDisplayed,
				picUrl: field.file ? field.file : undefined,
				link1: JSON.stringify(data.linkA) || "",
				link2: JSON.stringify(data.linkB) || "",
				link3: JSON.stringify(data.linkC) || "",
				link4: JSON.stringify(data.linkD) || "",
				link5: JSON.stringify(data.linkE) || "",
				link6: JSON.stringify(data.linkF) || "",
				areas: JSON.stringify(state.default.areas),
				imgType: state.default.imgType,
				template: state.default.imgSelect,
			};

			richmenuUpdateMutation.mutate({ ...paramData });
		} else if (type === "memberRM") {
			const data = memberRichmenuForm.getFieldsValue(true);

			const paramData = {
				type: type,
				isDisplayed: richmenus.memberRM?.isDisplayed,
				picUrl: field.file ? field.file : undefined,
				link1: JSON.stringify(data.linkA) || "",
				link2: JSON.stringify(data.linkB) || "",
				link3: JSON.stringify(data.linkC) || "",
				link4: JSON.stringify(data.linkD) || "",
				link5: JSON.stringify(data.linkE) || "",
				link6: JSON.stringify(data.linkF) || "",
				areas: JSON.stringify(state.member.areas),
				imgType: state.member.imgType,
				template: state.member.imgSelect,
			};

			richmenuUpdateMutation.mutate({ ...paramData });
		}
	};

	const handleRichmenuDelete = (type) => {
		const paramData = {
			type: type,
		};

		modal.confirm({
			title: "確認",
			icon: <ExclamationCircleOutlined className="text-red-600" />,
			content: "リッチメニューを削除してもよろしいでしょうか？",
			okText: "削除",
			okButtonProps: {
				size: "large",
				type: "primary",
				danger: true,
			},
			cancelText: "閉じる",
			cancelButtonProps: {
				size: "large",
			},
			centered: true,
			onOk() {
				richmenuDeleteMutation.mutate(paramData, type);
			},
		});
	};

	return (
		<>
			<Modal
				title="リッチメニュー設定"
				open={isRichmenuModalVisible}
				onCancel={hideRichmenuModal}
				footer={null}
				getContainer={false}
				destroyOnClose
				centered
				styles={{
					body: {
						maxHeight: "90vh",
						overflowY: "auto",
						overflowX: "hidden",
					},
				}}
				afterClose={() => setInitialValue()}
			>
				<motion.div
					className="flex flex-col"
					variants={COMMONS.ANIMATION_VARIANT_STAGGER_CONTAINER}
					initial="hidden"
					animate="show"
					exit="hidden"
				>
					<Divider>登録前のリッチメニュー</Divider>
					<div className="mt-4 mb-4">
						<Radio.Group
							size="large"
							optionType="button"
							buttonStyle="solid"
							options={[
								{ label: "小", value: "compact" },
								{ label: "大", value: "large" },
							]}
							value={state.default.imgType}
							defaultValue={state.default.imgType || "compact"}
							onChange={(e) => {
								e.target.value === "compact"
									? dispatch({
											type: SET_DATA_DEFAULT_USER,
											payload: {
												...state.default,
												imgDefault: "/template-before.png",
												inputNumber: 2,
												imgSelect: "",
												imgType: "compact",
											},
										})
									: dispatch({
											type: SET_DATA_DEFAULT_USER,
											payload: {
												...state.default,
												imgDefault: "/template-after.png",
												inputNumber: 5,
												imgSelect: "",
												imgType: "large",
											},
										});
								defaultRichmenuForm.resetFields();
								setDefaultRichmenuImageFile(undefined);
							}}
						/>
					</div>
					<SelectLayout
						type="default"
						templateType={state.default.imgType}
						imgSrc={state.default.imgSelect}
						imgDefault={state.default.imgDefault}
						handleSelectTemplate={() => handleSelectTemplate("default")}
					>
						{state.default.imgType === "compact" ? (
							<>
								<p className="my-4">
									リッチメニューの画像は以下の要件を満たす必要があります
								</p>
								<ol className="custom-ol list-none list-outside mb-2">
									<li>画像フォーマット：JPEGまたはPNG</li>
									<li>画像の幅サイズ（ピクセル）：1200px</li>
									<li>画像の高さサイズ（ピクセル）：405px</li>
									<li>最大ファイルサイズ：1MB</li>
								</ol>
							</>
						) : (
							<>
								<p className="my-4">
									リッチメニューの画像は以下の要件を満たす必要があります
								</p>
								<ol className="custom-ol list-none list-outside mb-2">
									<li>画像フォーマット：JPEGまたはPNG</li>
									<li>画像の幅サイズ（ピクセル）：1200px</li>
									<li>画像の高さサイズ（ピクセル）：810px</li>
									<li>最大ファイルサイズ：1MB</li>
								</ol>
							</>
						)}
					</SelectLayout>
					<motion.div
						className="border border-custom-gray p-4"
						variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
					>
						<Form
							name="defaultRichmenuForm"
							form={defaultRichmenuForm}
							onFinish={handleRichmenuUpdate(
								"defaultRM",
								defaultRichmenuImageFile,
							)}
							layout="vertical"
							requiredMark={false}
							preserve={false}
							scrollToFirstError
						>
							<div className="flex flex-col">
								<div className="flex justify-end mb-4">
									<TapAnimationComponent>
										<Button
											danger
											icon={<DeleteOutlined />}
											onClick={() => {
												handleRichmenuDelete("defaultRM");
											}}
											loading={richmenuDeleteMutation.isLoading}
											disabled={richmenus.defaultRM ? false : true}
										/>
									</TapAnimationComponent>
								</div>
								<Form.Item
									name="richmenuImage"
									className="whitespace-pre-wrap text-center"
									help={`※このリッチメニューは、まだ会員ではないユーザーに\n表示されます`}
									valuePropName="file"
								>
									<CustomUpload
										accept=".jpg, .jpeg, .png"
										listType="picture"
										maxCount={1}
										showUploadList={false}
										beforeUpload={() => {
											return false;
										}}
										onChange={async (param) => {
											checkRichMenuImageSize(
												state.default.imgType,
												"defaultRM",
												param,
												setDefaultRichmenuImageFile,
											);
										}}
									>
										{defaultRichmenuImageFile?.preview ? (
											<img
												src={defaultRichmenuImageFile.preview}
												alt="登録前のリッチメニュー"
												style={{
													maxHeight: "250px",
												}}
												className="w-full object-contain cursor-pointer"
											/>
										) : (
											<div
												style={{
													height: "250px",
													backgroundColor: "#fafafa",
													border: "1px dashed #d9d9d9",
												}}
												className="flex justify-center items-center rounded cursor-pointer"
											>
												<TapAnimationComponent>
													<Button
														type="dashed"
														icon={
															<CameraTwoTone
																twoToneColor={publicSettings?.PRIMARY_COLOR}
															/>
														}
													>
														アップロード
													</Button>
												</TapAnimationComponent>
											</div>
										)}
									</CustomUpload>
								</Form.Item>
								<Divider />
								{new Array(state.default.inputNumber).fill().map((_, index) => {
									return (
										<Fragment key={index}>
											<Form.Item
												noStyle
												initialValue={INPUT_NAME[index].replace("link", "")}
												name={[INPUT_NAME[index], "position"]}
											/>
											<Form.Item
												noStyle
												shouldUpdate={(prevValues, currentValues) =>
													!isEqual(
														get(prevValues, INPUT_NAME[index]),
														get(currentValues, INPUT_NAME[index]),
													)
												}
											>
												{({ getFieldValue }) => {
													const type = getFieldValue([
														INPUT_NAME[index],
														"type",
													]);
													return (
														<>
															<TriggerValidation
																fieldNameWatched={[INPUT_NAME[index], "type"]}
																fieldNameValidated={[
																	INPUT_NAME[index],
																	"value",
																]}
															/>

															<Form.Item
																name={[INPUT_NAME[index], "value"]}
																className="mt-4 [&_.ant-form-item-explain-error]:ml-[132px]"
																label={INPUT_LABEL[index]}
																rules={[
																	{
																		required: true,
																		message: "入力は必須です",
																	},
																	...(type === "uri"
																		? [
																				{
																					type: "url",
																					message:
																						"入力のURLが有効なURLではありません",
																				},
																			]
																		: []),
																]}
																key={`link-${index}`}
															>
																<Input
																	className="[&_.ant-input-group-addon]:text-left [&&&_.ant-input-group-addon]:border-[#d9d9d9]"
																	addonBefore={
																		<AddonBefore
																			name={[INPUT_NAME[index], "type"]}
																		/>
																	}
																	placeholder={`例：${API.SITE_URL}`}
																	onPressEnter={(e) => e.preventDefault()}
																	allowClear
																/>
															</Form.Item>
														</>
													);
												}}
											</Form.Item>
										</Fragment>
									);
								})}
								<Divider />
								<div className="flex justify-center">
									<TapAnimationComponent>
										<Button
											type="primary"
											htmlType="submit"
											loading={richmenuUpdateMutation.isLoading}
											disabled={
												defaultRichmenuImageFile?.file ||
												defaultRichmenuForm.getFieldValue("richmenuImage")
													? false
													: true
											}
										>
											設定する
										</Button>
									</TapAnimationComponent>
								</div>
							</div>
						</Form>
					</motion.div>
					<Divider>登録後のリッチメニュー</Divider>
					<div className="mt-4 mb-4">
						<Radio.Group
							size="large"
							optionType="button"
							buttonStyle="solid"
							options={[
								{ label: "小", value: "compact" },
								{ label: "大", value: "large" },
							]}
							value={state.member.imgType}
							defaultValue={state.member.imgType || "large"}
							onChange={(e) => {
								e.target.value === "compact"
									? dispatch({
											type: SET_DATA_MEMBER_USER,
											payload: {
												...state.member,
												imgDefault: "/template-before.png",
												inputNumber: 2,
												imgSelect: "",
												imgType: "compact",
											},
										})
									: dispatch({
											type: SET_DATA_MEMBER_USER,
											payload: {
												...state.member,
												imgDefault: "/template-after.png",
												inputNumber: 5,
												imgSelect: "",
												imgType: "large",
											},
										});
								memberRichmenuForm.resetFields();
								setMemberRichmenuImageFile(undefined);
							}}
						/>
					</div>
					<SelectLayout
						type="member"
						templateType={state.member.imgType}
						imgSrc={state.member.imgSelect}
						imgDefault={state.member.imgDefault}
						handleSelectTemplate={() => handleSelectTemplate("member")}
					>
						{state.member.imgType === "compact" ? (
							<>
								<p className="my-4">
									リッチメニューの画像は以下の要件を満たす必要があります
								</p>
								<ol className="custom-ol list-none list-outside mb-2">
									<li>画像フォーマット：JPEGまたはPNG</li>
									<li>画像の幅サイズ（ピクセル）：1200px</li>
									<li>画像の高さサイズ（ピクセル）：405px</li>
									<li>最大ファイルサイズ：1MB</li>
								</ol>
							</>
						) : (
							<>
								<p className="my-4">
									リッチメニューの画像は以下の要件を満たす必要があります
								</p>
								<ol className="custom-ol list-none list-outside mb-2">
									<li>画像フォーマット：JPEGまたはPNG</li>
									<li>画像の幅サイズ（ピクセル）：1200px</li>
									<li>画像の高さサイズ（ピクセル）：810px</li>
									<li>最大ファイルサイズ：1MB</li>
								</ol>
							</>
						)}
					</SelectLayout>
					<motion.div
						className="border border-custom-gray p-4"
						variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
					>
						<Form
							name="memberRichmenuForm"
							form={memberRichmenuForm}
							onFinish={handleRichmenuUpdate(
								"memberRM",
								memberRichmenuImageFile,
							)}
							layout="vertical"
							requiredMark={false}
							preserve={false}
							initialValues={{
								richmenuImage: undefined,
							}}
							scrollToFirstError
						>
							<div className="flex flex-col">
								<div className="flex justify-end mb-4">
									<TapAnimationComponent>
										<Button
											danger
											icon={<DeleteOutlined />}
											onClick={() => {
												handleRichmenuDelete("memberRM");
											}}
											loading={richmenuDeleteMutation.isLoading}
											disabled={richmenus.memberRM ? false : true}
										/>
									</TapAnimationComponent>
								</div>
								<Form.Item
									name="richmenuImage"
									className="whitespace-pre-wrap text-center"
									help={`※このリッチメニューは、会員のユーザーに\n表示されます`}
									valuePropName="file"
								>
									<CustomUpload
										accept=".jpg, .jpeg, .png"
										listType="picture"
										maxCount={1}
										showUploadList={false}
										beforeUpload={() => {
											return false;
										}}
										onChange={async (param) => {
											checkRichMenuImageSize(
												state.member.imgType,
												"memberRM",
												param,
												setMemberRichmenuImageFile,
											);
										}}
									>
										{memberRichmenuImageFile?.preview ? (
											<img
												src={memberRichmenuImageFile.preview}
												alt="登録前のリッチメニュー"
												style={{
													maxHeight: "250px",
												}}
												className="w-full object-contain cursor-pointer"
											/>
										) : (
											<div
												style={{
													height: "250px",
													backgroundColor: "#fafafa",
													border: "1px dashed #d9d9d9",
												}}
												className="flex justify-center items-center rounded cursor-pointer"
											>
												<TapAnimationComponent>
													<Button
														type="dashed"
														icon={
															<CameraTwoTone
																twoToneColor={publicSettings?.PRIMARY_COLOR}
															/>
														}
													>
														アップロード
													</Button>
												</TapAnimationComponent>
											</div>
										)}
									</CustomUpload>
								</Form.Item>
								<Divider />
								{new Array(state.member.inputNumber).fill().map((_, index) => {
									return (
										<Fragment key={index}>
											<Form.Item
												noStyle
												initialValue={INPUT_NAME[index].replace("link", "")}
												name={[INPUT_NAME[index], "position"]}
											/>
											<Form.Item
												noStyle
												shouldUpdate={(prevValues, currentValues) =>
													!isEqual(
														get(prevValues, INPUT_NAME[index]),
														get(currentValues, INPUT_NAME[index]),
													)
												}
											>
												{({ getFieldValue }) => {
													const type = getFieldValue([
														INPUT_NAME[index],
														"type",
													]);
													return (
														<Form.Item
															name={[INPUT_NAME[index], "value"]}
															className="mt-4 [&_.ant-form-item-explain-error]:ml-[132px]"
															label={INPUT_LABEL[index]}
															rules={[
																{
																	required: true,
																	message: "入力は必須です",
																},
																...(type === "uri"
																	? [
																			{
																				type: "url",
																				message:
																					"入力のURLが有効なURLではありません",
																			},
																		]
																	: []),
															]}
															key={`link-${index}`}
														>
															<Input
																className="[&_.ant-input-group-addon]:text-left [&&&_.ant-input-group-addon]:border-[#d9d9d9]"
																addonBefore={
																	<AddonBefore
																		name={[INPUT_NAME[index], "type"]}
																	/>
																}
																placeholder={`例：${API.SITE_URL}`}
																onPressEnter={(e) => e.preventDefault()}
																allowClear
															/>
														</Form.Item>
													);
												}}
											</Form.Item>
										</Fragment>
									);
								})}
								<Divider />
								<div className="flex justify-center">
									<TapAnimationComponent>
										<Button
											type="primary"
											htmlType="submit"
											loading={richmenuUpdateMutation.isLoading}
											disabled={
												memberRichmenuImageFile?.file ||
												memberRichmenuForm.getFieldValue("richmenuImage")
													? false
													: true
											}
										>
											設定する
										</Button>
									</TapAnimationComponent>
								</div>
							</div>
						</Form>
					</motion.div>
				</motion.div>
			</Modal>
			<Modal
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{ style: { display: "none" } }}
				open={isOpenImageTemplate}
				maskClosable
				onCancel={() => setIsOpenImageTemplate(!isOpenImageTemplate)}
			>
				<ImageTemplateContainer
					selected={selected}
					setIsOpenImageTemplate={setIsOpenImageTemplate}
					dispatcher={dispatch}
					state={state}
				/>
			</Modal>
			{contextHolder}
		</>
	);
};

export default RichmenuModal;
